import { UWQueueDashboardStats } from "@/models/underwriting";
import { formatDuration, outcomeSplit } from "@/helpers/underwritingQueueDashboard";

function QueueStatCard({ title, value }: { title: string; value: React.ReactNode }) {
  return (
    <div className="relative flex flex-col gap-1 rounded-lg border border-border bg-card px-4 pb-4 pt-3 shadow-sm before:absolute before:inset-x-0 before:top-0 before:h-1 before:rounded-t-lg before:bg-teal-600">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <p className="text-3xl font-bold leading-none text-foreground">{value}</p>
    </div>
  );
}

/**
 * Allocated vs deaded, shown as counts and shares side by side. The split is the
 * point of the card, so it gets its own panel rather than two more stat tiles.
 */
function OutcomeSplitCard({ stats }: { stats: UWQueueDashboardStats }) {
  const split = outcomeSplit(stats);

  const rows = [
    { label: "Allocated", count: split.allocated, pct: split.allocatedPct },
    { label: "Deaded", count: split.deaded, pct: split.deadedPct },
  ];

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
      <p className="text-sm font-medium text-foreground">Outcomes</p>

      <div className="mt-2 flex flex-col divide-y divide-border">
        {rows.map(({ label, count, pct }) => (
          <div key={label} className="flex items-center justify-between py-2 text-sm">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums text-foreground">
              <span className="font-semibold">{count}</span>
              <span className="ml-2 text-muted-foreground">{pct}%</span>
            </span>
          </div>
        ))}
      </div>

      {split.outside > 0 && (
        <p
          className="mt-2 border-t border-border pt-2 text-[11px] leading-snug text-muted-foreground"
          title="Applications that left the queue without an action being recorded in this portal"
        >
          <span className="font-medium text-foreground">{split.outside}</span> left the queue outside
          the portal
        </p>
      )}
    </div>
  );
}

export function QueueStatCardsSection({
  stats,
  inQueueCount,
  oldestInQueueMinutes,
}: {
  stats: UWQueueDashboardStats;
  /** Length of the visible applications list — not derived from the stats API. */
  inQueueCount: number;
  /** Oldest allocation age within the visible applications list. */
  oldestInQueueMinutes: number | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-4 lg:items-stretch">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:col-span-3">
        <QueueStatCard title="In Queue" value={inQueueCount} />
        <QueueStatCard
          title="Oldest In Queue"
          value={formatDuration(oldestInQueueMinutes)}
        />
        <QueueStatCard
          title="Avg Turnaround"
          value={formatDuration(stats.avgTurnaroundMinutes)}
        />
        <QueueStatCard title="Actioned" value={stats.actionedCount} />
        <QueueStatCard title="Allocated" value={stats.allocatedCount} />
        <QueueStatCard title="Deaded" value={stats.deadedCount} />
      </div>

      <OutcomeSplitCard stats={stats} />
    </div>
  );
}
